import React, { FC } from 'react';

import { Flex } from '@adsk/alloy-react-box';
import { LinkButton } from '@adsk/alloy-react-button';
import {
  AlertWarningFilledIcon,
  CheckmarkCircleFilledIcon,
} from '@adsk/alloy-react-icon';
import ProgressRing from '@adsk/alloy-react-progress-ring';
import { WithAlloySuspense } from '@adsk/alloy-react-suspense';
import theme from '@adsk/alloy-react-theme';
import Tile from '@adsk/alloy-react-tile';
import Tooltip from '@adsk/alloy-react-tooltip';

import { ATTACHMENTS_LIST_VARIANTS } from '../../../consts';
import { useTranslation } from '../../../i18n';
import { AttachmentItemViewProps } from '../../../types';
import AttachmentContextualMenu from '../AttachmentItemContextualMenu';
import AttachmentItemIcon from '../AttachmentItemIcon';

const AttachmentTileItem: FC<AttachmentItemViewProps> = ({
  name,
  metadata,
  thumbnail,
  isContextualMenuOpen,
  setIsContextualMenuOpen,
  allowDelete,
  allowDownload,
  onDelete,
  error,
  allowRetry,
  onClick,
  onDownload,
  onRetry,
  isDeleting,
  isUploading,
  customItemActions,
  showUploadSuccessIndication,
  style,
}) => {
  const { t } = useTranslation();

  const getSubtitle = () => {
    if (error) {
      return t('UPLOAD_FAILED');
    }

    if (isDeleting) {
      return t('DELETING_DOTS');
    }

    if (isUploading) {
      return t('UPLOADING_DOTS');
    }

    return metadata;
  };

  const isLoading = (isDeleting || isUploading) && !error;

  return (
    <Tile
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();

        if (onClick) {
          onClick();
        }
      }}
      title={name}
      subtitle={getSubtitle()}
      image={thumbnail}
      renderImage={
        !thumbnail || error
          ? () => {
              if (error) {
                return (
                  <Flex
                    alignItems="center"
                    justifyContent="center"
                    height="100%"
                    flexDirection="column"
                  >
                    <Tooltip
                      content={error}
                      data-testid="attachmentErrorTooltip"
                    >
                      <AlertWarningFilledIcon
                        color={theme.colors.red500}
                        style={{ marginBottom: 8 }}
                      />
                    </Tooltip>
                    {allowRetry && (
                      <LinkButton
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();

                          if (onRetry) {
                            onRetry();
                          }
                        }}
                        style={{ marginTop: 4 }}
                      >
                        {t('RETRY')}
                      </LinkButton>
                    )}
                  </Flex>
                );
              }

              return (
                <Flex alignItems="center" justifyContent="center" height="100%">
                  <AttachmentItemIcon name={name} />
                </Flex>
              );
            }
          : undefined
      }
      style={{
        width: '100%',
        height: '100%',
        '& img': {
          userSelect: 'none',
          userDrag: 'none',
        },
        ...(!isLoading && !error
          ? {
              '& label': {
                cursor: 'pointer',
              },
            }
          : {}),
        ...(isLoading || error
          ? {
              color: theme.colors.charcoal400,
            }
          : {}),
        ...style,
      }}
      key={error}
      renderOverlay={() => null}
      renderActions={() => {
        if (isLoading) {
          return (
            <ProgressRing
              data-testid="ProjectsLoader"
              size={ProgressRing.SIZES.XSMALL}
              style={{ marginLeft: 8, flexShrink: 0 }}
            />
          );
        }

        if (showUploadSuccessIndication) {
          return (
            <CheckmarkCircleFilledIcon
              color={theme.tokens.colors.icon.success.value}
              size={16}
            />
          );
        }

        if (!isLoading && !error) {
          return (
            <AttachmentContextualMenu
              show={isContextualMenuOpen}
              onHide={() => setIsContextualMenuOpen(false)}
              onShow={() => setIsContextualMenuOpen(true)}
              allowDelete={allowDelete}
              onDelete={onDelete}
              allowDownload={allowDownload}
              onDownload={onDownload}
              variant={ATTACHMENTS_LIST_VARIANTS.GRID}
              customItemActions={customItemActions}
            />
          );
        }

        return null as unknown as React.ReactElement;
      }}
    />
  );
};

export default WithAlloySuspense(AttachmentTileItem);
